import { useState, useEffect, useCallback } from "react";
import { LifeBuoy, Plus, Loader2, X, MessageSquare, ChevronDown } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import TicketThread, { TYPE_LABELS, STATUS_LABELS, STATUS_COLORS, PRIORITY_LABELS } from "./TicketThread";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" }); } catch { return iso; } };

/**
 * TicketsSection (Händler-Profil) — eigene Tickets auflisten, neues Ticket
 * erstellen und den Konversations-Thread öffnen. Für Probleme, Erweiterungs-
 * wünsche und Support bei Installation/Anwendung.
 */
export default function TicketsSection() {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openId, setOpenId] = useState(null);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ subject: "", type: "support", priority: "normal", body: "" });
    const [submitting, setSubmitting] = useState(false);
    const [collapsed, setCollapsed] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try { const { data } = await api.get("/tickets"); setTickets(data.tickets || []); }
        catch { /* ignore */ }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const submit = async () => {
        if (!form.subject.trim() || !form.body.trim()) { toast.error("Betreff und Beschreibung erforderlich"); return; }
        setSubmitting(true);
        try {
            const { data } = await api.post("/tickets", form);
            toast.success("Ticket erstellt");
            setForm({ subject: "", type: "support", priority: "normal", body: "" });
            setCreating(false);
            await load();
            setOpenId(data.id);
        } catch (e) { toast.error(e.response?.data?.detail || "Ticket konnte nicht erstellt werden"); }
        finally { setSubmitting(false); }
    };

    return (
        <div className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid="tickets-section">
            <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-200 bg-[#FFFBEB]/40">
                <LifeBuoy className="w-5 h-5 text-[#FFCC00] shrink-0" />
                <button type="button" onClick={() => setCollapsed((v) => !v)} className="flex-1 min-w-0 text-left" data-testid="tickets-section-toggle">
                    <h2 className="font-display text-lg font-bold">Support / Tickets</h2>
                    <p className="text-xs text-gray-600 mt-1">Probleme melden, Erweiterungen wünschen oder Hilfe bei Installation & Anwendung erhalten.</p>
                </button>
                <button onClick={() => { setCollapsed(false); setCreating((v) => !v); }} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm bg-[#111827] hover:bg-black text-white text-sm font-bold shrink-0" data-testid="ticket-new-btn">
                    <Plus className="w-4 h-4" /> Neues Ticket
                </button>
                <button type="button" onClick={() => setCollapsed((v) => !v)} className="shrink-0 text-gray-500 hover:text-gray-700" data-testid="tickets-section-chevron" aria-label={collapsed ? "Tickets-Bereich ausklappen" : "Tickets-Bereich einklappen"}>
                    <ChevronDown className={`w-5 h-5 transition-transform ${collapsed ? "" : "rotate-180"}`} />
                </button>
            </div>

            {!collapsed && (<>

            {creating && (
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50" data-testid="ticket-create-form">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                        <div className="sm:col-span-3">
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Betreff</label>
                            <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Kurzbeschreibung" className="w-full mt-1 h-10 rounded-sm border border-gray-300 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#111827]" data-testid="ticket-subject-input" />
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Typ</label>
                            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full mt-1 h-10 rounded-sm border border-gray-300 px-2 text-sm" data-testid="ticket-type-select">
                                {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Priorität</label>
                            <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="w-full mt-1 h-10 rounded-sm border border-gray-300 px-2 text-sm" data-testid="ticket-priority-select">
                                {Object.entries(PRIORITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                            </select>
                        </div>
                    </div>
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Beschreibung</label>
                    <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={4} placeholder="Beschreiben Sie Ihr Anliegen …" className="w-full mt-1 rounded-sm border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#111827] resize-none" data-testid="ticket-body-input" />
                    <div className="flex justify-end gap-2 mt-3">
                        <button onClick={() => setCreating(false)} className="h-9 px-4 rounded-sm border border-gray-300 text-sm font-bold">Abbrechen</button>
                        <button onClick={submit} disabled={submitting} className="h-9 px-4 rounded-sm bg-[#111827] hover:bg-black text-white text-sm font-bold disabled:opacity-60 inline-flex items-center gap-1.5" data-testid="ticket-submit-btn">
                            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Ticket erstellen
                        </button>
                    </div>
                </div>
            )}

            <div className="divide-y divide-gray-100">
                {loading ? (
                    <div className="flex items-center justify-center py-10 text-gray-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
                ) : tickets.length === 0 ? (
                    <div className="px-6 py-10 text-center text-sm text-gray-400" data-testid="tickets-empty">Noch keine Tickets. Erstellen Sie Ihr erstes Ticket.</div>
                ) : (
                    tickets.map((t) => (
                        <button key={t.id} onClick={() => setOpenId(t.id)} className="w-full text-left px-6 py-3 hover:bg-gray-50 flex items-center gap-3" data-testid={`ticket-row-${t.id}`}>
                            <MessageSquare className="w-4 h-4 text-gray-300 shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="font-bold text-sm truncate">{t.subject}</div>
                                <div className="text-[11px] text-gray-500 flex items-center gap-2 mt-0.5">
                                    <span>{TYPE_LABELS[t.type]}</span>
                                    <span>· {t.message_count} Nachr.</span>
                                    <span>· {fmtDate(t.last_activity_at)}</span>
                                </div>
                            </div>
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm shrink-0 ${STATUS_COLORS[t.status]}`}>{STATUS_LABELS[t.status]}</span>
                        </button>
                    ))
                )}
            </div>
            </>)}

            {openId && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4" data-testid="ticket-detail-modal">
                    <div className="bg-white rounded-sm w-full max-w-2xl p-5 max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => setOpenId(null)} className="self-end p-1 text-gray-400 hover:text-gray-700 -mt-1 -mr-1" data-testid="ticket-detail-close"><X className="w-5 h-5" /></button>
                        <TicketThread ticketId={openId} onChanged={load} />
                    </div>
                </div>
            )}
        </div>
    );
}
